import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { debounce } from '@/utils/debounce';
import { useSearchStore } from '@/stores/search';
import { useTurnstile } from '@/composables/useTurnstile';
import type { SearchAppliedFilters } from '@/types';

type SearchOptions = {
  page?: number;
  perPage?: number;
  skipUrlSync?: boolean;
};

let pendingVerificationPromise: Promise<void> | null = null;

const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (typeof value !== 'string' || value.length === 0) {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const toStringOrUndefined = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || value.length === 0) {
    return undefined;
  }

  return value;
};

export function useSearch() {
  const route = useRoute();
  const router = useRouter();
  const searchStore = useSearchStore();
  const { showInvisible } = useTurnstile();

  const {
    query,
    results,
    suggestions,
    filters,
    appliedFilters,
    pagination,
    trending,
    popular,
    loading,
    error,
    requiresVerification,
    turnstileSiteKey,
    retryAfter,
  } = storeToRefs(searchStore);

  const searchRequestTimestamp = ref(0);
  const suggestionRequestTimestamp = ref(0);

  const nextTimestamp = () => Date.now() + Math.floor(Math.random() * 1000);

  const executeSearch = async (
    searchQuery: string,
    filtersPayload: SearchAppliedFilters = {},
    options: SearchOptions = {}
  ): Promise<void> => {
    const requestTimestamp = nextTimestamp();
    searchRequestTimestamp.value = requestTimestamp;

    await searchStore.executeSearch(searchQuery, filtersPayload, {
      page: options.page,
      perPage: options.perPage,
    });

    if (searchRequestTimestamp.value !== requestTimestamp) {
      return;
    }

    if (!options.skipUrlSync) {
      updateUrl(searchQuery, filtersPayload, options.page, options.perPage);
    }

    if (requiresVerification.value) {
      await verifyAndRetry();
    }
  };

  const runAutocomplete = async (searchQuery: string): Promise<void> => {
    if (searchQuery.trim().length < 3) {
      suggestions.value = [];
      return;
    }

    const requestTimestamp = nextTimestamp();
    suggestionRequestTimestamp.value = requestTimestamp;

    await searchStore.fetchSuggestions(searchQuery);

    if (suggestionRequestTimestamp.value !== requestTimestamp) {
      return;
    }

    if (requiresVerification.value) {
      await verifyAndRetry();
    }
  };

  const debouncedAutocomplete = debounce((searchQuery: string) => {
    void runAutocomplete(searchQuery);
  }, 300);

  const buildQueryObject = (
    searchQuery: string,
    filtersPayload: SearchAppliedFilters,
    page?: number,
    perPage?: number
  ): Record<string, string> => {
    const queryObject: Record<string, string> = {};

    if (searchQuery.trim().length > 0) {
      queryObject.q = searchQuery.trim();
    }

    const keys: Array<keyof SearchAppliedFilters> = [
      'category_id',
      'photographer_profile_id',
      'model_profile_id',
      'price_min',
      'price_max',
    ];

    keys.forEach((key) => {
      const value = filtersPayload[key];
      if (value !== undefined && value !== null && value !== '') {
        queryObject[key] = String(value);
      }
    });

    if (page && page > 1) {
      queryObject.page = String(page);
    }

    if (perPage && perPage !== 12) {
      queryObject.per_page = String(perPage);
    }

    return queryObject;
  };

  const updateUrl = (
    searchQuery: string,
    filtersPayload: SearchAppliedFilters,
    page = 1,
    perPage = 12
  ): void => {
    const nextQuery = buildQueryObject(searchQuery, filtersPayload, page, perPage);
    const currentQuery = route.query;

    const isSameQuery = Object.keys(nextQuery).every((key) => nextQuery[key] === currentQuery[key]);
    const sameLength = Object.keys(nextQuery).length === Object.keys(currentQuery).length;

    if (isSameQuery && sameLength) {
      return;
    }

    void router.push({
      path: '/search',
      query: nextQuery,
    });
  };

  const parseFiltersFromRoute = (): SearchAppliedFilters => {
    return {
      category_id: toStringOrUndefined(route.query.category_id),
      photographer_profile_id: toStringOrUndefined(route.query.photographer_profile_id),
      model_profile_id: toStringOrUndefined(route.query.model_profile_id),
      price_min: toNumberOrUndefined(route.query.price_min),
      price_max: toNumberOrUndefined(route.query.price_max),
    };
  };

  const syncWithUrl = async (): Promise<void> => {
    const searchQuery = toStringOrUndefined(route.query.q) ?? '';
    const parsedFilters = parseFiltersFromRoute();
    const page = toNumberOrUndefined(route.query.page) ?? 1;
    const perPage = toNumberOrUndefined(route.query.per_page) ?? 12;

    if (!searchQuery && !Object.values(parsedFilters).some((value) => value !== undefined)) {
      return;
    }

    await executeSearch(searchQuery, parsedFilters, {
      page,
      perPage,
      skipUrlSync: true,
    });
  };

  const verifyAndRetry = async (): Promise<void> => {
    if (!requiresVerification.value) {
      return;
    }

    if (pendingVerificationPromise) {
      return await pendingVerificationPromise;
    }

    const fallbackSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
    const siteKey = turnstileSiteKey.value ?? fallbackSiteKey;

    if (!siteKey) {
      return;
    }

    const verificationPromise = (async () => {
      const token = await showInvisible(siteKey);
      await searchStore.verifyWithTurnstile(token);
    })();

    pendingVerificationPromise = verificationPromise;

    try {
      await verificationPromise;
    } finally {
      if (pendingVerificationPromise === verificationPromise) {
        pendingVerificationPromise = null;
      }
    }
  };

  const retryCurrentSearch = async (): Promise<void> => {
    await executeSearch(query.value, appliedFilters.value, {
      page: pagination.value.current_page,
      perPage: pagination.value.per_page,
    });
  };

  const hasActiveFilters = computed(() => Object.values(appliedFilters.value).some((value) => value !== undefined));

  return {
    query,
    results,
    suggestions,
    filters,
    appliedFilters,
    pagination,
    trending,
    popular,
    loading,
    error,
    requiresVerification,
    retryAfter,
    hasActiveFilters,
    executeSearch,
    debouncedAutocomplete,
    fetchAvailableFilters: searchStore.fetchAvailableFilters,
    fetchTrending: searchStore.fetchTrending,
    fetchPopular: searchStore.fetchPopular,
    syncWithUrl,
    parseFiltersFromRoute,
    updateUrl,
    retryCurrentSearch,
    verifyAndRetry,
  };
}
